import { findDuplicateCsvHeaders, readCsvHeaders } from "helpers/csvHeaders";
import Papa from "papaparse";

export type ParsedCsvFile = {
    loadedData: any[];
    fileTitles: string[];
    duplicateHeaders: string[];
    delimiter: string;
};

const emptyParsedCsvFile = (duplicateHeaders: string[]): ParsedCsvFile => ({
    loadedData: [],
    fileTitles: [],
    duplicateHeaders,
    delimiter: "",
});

const normalizeCsvCell = (value: string, column: string, floatKeys: string[]) =>
    floatKeys.includes(column) ? value.replaceAll(",", ".") : value;

const appendCsvCell = (row: Record<string, any>, column: string, value: string, floatKeys: string[]) => {
    const cell = normalizeCsvCell(value, column, floatKeys);
    if (row[column]) {
        row[column].push(cell);
        return;
    }
    row[column] = [cell];
};

const collapseSingleCellColumns = (row: Record<string, any>) => {
    Object.keys(row).forEach((key) => {
        if (row[key].length === 1) {
            row[key] = row[key][0];
        }
    });
};

const rowFromCsvCells = (cells: string[], headers: string[], floatKeys: string[]) => {
    const row: Record<string, any> = {};
    cells.forEach((value, index) => {
        appendCsvCell(row, headers[index], value, floatKeys);
    });
    collapseSingleCellColumns(row);
    return row;
};

const rowsFromCsvMatrix = (data: string[][], floatKeys: string[]) => {
    const headers = data[0] ?? [];
    return {
        loadedData: data.slice(1).map((cells) => rowFromCsvCells(cells, headers, floatKeys)),
        fileTitles: headers.filter((value, index, self) => self.indexOf(value) === index),
    };
};

const replaceCommasInFloatColumns = (line: Record<string, string>, floatKeys: string[]) => {
    for (const cell in line) {
        if (floatKeys.includes(cell)) {
            line[cell] = line[cell].replaceAll(",", ".");
        }
    }
    return line;
};

const parseFileUnlessDuplicateHeaders = (
    file: File,
    onComplete: (data: ParsedCsvFile) => void,
    parseRows: () => void,
    singleValueHeaders?: readonly string[],
) => {
    readCsvHeaders(file, (headers) => {
        const duplicateHeaders = findDuplicateCsvHeaders(
            headers,
            singleValueHeaders ? { singleValueHeaders } : undefined,
        );
        if (duplicateHeaders.length) {
            onComplete(emptyParsedCsvFile(duplicateHeaders));
            return;
        }
        parseRows();
    });
};

/**
 * Forms data of import file and returns fileData and fileTitles.
 * Repeated channel headers are kept as arrays. A header that belongs to a
 * single-value column is rejected before rows are built.
 */
export const formatDataFromFile = (
    {
        file,
        floatKeys,
        singleValueHeaders,
    }: { file: File; floatKeys: string[]; singleValueHeaders?: readonly string[] },
    onComplete: (data: ParsedCsvFile) => void,
) => {
    parseFileUnlessDuplicateHeaders(
        file,
        onComplete,
        () => {
            Papa.parse<string[]>(file, {
                skipEmptyLines: true,
                complete: (results) => {
                    const { loadedData, fileTitles } = rowsFromCsvMatrix(results.data, floatKeys);
                    onComplete({
                        loadedData,
                        fileTitles,
                        duplicateHeaders: [],
                        delimiter: results.meta.delimiter ?? "",
                    });
                },
            });
        },
        singleValueHeaders,
    );
};

/** Sales and stock files: one object per row, keys taken from the header. */
export const parseCsvWithHeader = (
    { file, floatKeys }: { file: File; floatKeys: string[] },
    onComplete: (data: ParsedCsvFile) => void,
) => {
    parseFileUnlessDuplicateHeaders(file, onComplete, () => {
        Papa.parse<Record<string, string>>(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const loadedData = results.data.map((line) => replaceCommasInFloatColumns(line, floatKeys));
                onComplete({
                    loadedData,
                    fileTitles: Object.keys(results.data[0] ?? {}),
                    duplicateHeaders: [],
                    delimiter: results.meta.delimiter ?? "",
                });
            },
        });
    });
};

/**
 * Converts column number into letter as Excel columns.
 * @param columnNumber must be at least 1
 */
export const convertColumnIndexToTitle = (columnNumber: number): string => {
    /**
     * Maps each column to its corresponding letter {1: A, 2: B, ...}
     */
    const numberToCharacterMap = {};

    // Updates map with each letter of the alphabet.
    for (let i = 1; i <= 26; i++) {
        const character = String.fromCharCode(i + 64);
        numberToCharacterMap[i] = character;
    }

    let resp = "";
    while (columnNumber > 0) {
        const remainder = columnNumber % 26;
        if (remainder === 0) {
            resp = "Z" + resp;
            columnNumber = Math.floor(columnNumber / 26) - 1;
        } else {
            // If remainder > 0, we update resp then column number.
            // Ex: If column number is 27, resp = A and columnNumber = 1
            // On second loop, resp = AA and columnNumber = 0
            resp = numberToCharacterMap[remainder] + resp;
            columnNumber = Math.floor(columnNumber / 26);
        }
    }
    return resp;
};
